import { useState } from "react";

import {
  makeGetApiCall,
  makePostApiCall,
  makePostApiCallToPrinter,
} from "../../shared/makeApiCall";
import DeleteConfirmationForm from "../../components/forms/DeleteConfirmationForm";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import PackagingDetails from "../../components/forms/packaging/PackagingDetails";
import FilterSingleSelectDropdown from "../../components/filterDropdown/FilterSingleSelectDropdown";
import { SnackbarData, SnackbarType } from "../../components/Snackbar";
import { generateUniqueNumber } from "../../shared/utility";
import subDays from "date-fns/subDays";

const reportTypeOptions = [{ value: 1, label: "Packaging Report" }];

export function PackagingRecordsViewModel(logout, addSnackbar) {
  const defaultFilters = {
    product: { value: -1, label: "All Products" },
    category: { value: -1, label: "All Categories" },
    supervisor: { value: -1, label: "All Employees" },
    employee1: { value: -1, label: "All Employees" },
    brand: { value: -1, label: "All Brands" },
    startDate: subDays(new Date(), 7),
    endDate: new Date(),
  };
  const [filters, setFilters] = useState(defaultFilters);

  const [analytics, setAnalytics] = useState({
    range: null,
    stats: [],
    loading: true,
  });
  const [brands, setBrands] = useState([]);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [supervisorOptions, setSupervisorOptions] = useState([]);
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });

  const showErrorModal = (title, subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title={title}
        />
      ),
      open: true,
    });
  };
  const [loading, setLoading] = useState(false);
  const [packagings, setPackagings] = useState({
    data: [],
  });
  const [slideOver, setSlideOver] = useState({
    childComponent: null,
    open: false,
  });
  const [printingLabelType, setPrintingLabelType] = useState({ type: 2 });
  const [showFilters, setShowFilters] = useState(false);
  const [showDates, setShowDates] = useState(false);
  const [packagingLabel, setPackagingLabel] = useState({
    brand_image1: null,
    brand_image2: null,
    brand_image3: null,
    brand_name: null,
    address: null,
    lot_number: null,
    product_plu: null,
    ingredients: null,
    product_name: null,
    product_description: null,
    passed_from_metal_detector: null,
    product_weight: null,
    barcodeHtml: null,
    barcode: null,
    printCounter: 0,
    production_date: null,
    best_before_date: null,
  });
  const [bulkActionInProgress, setBulkActionInProgress] = useState({
    reprintAllLot: null,
    barcode: "",
  });
  const [reportFilters, setReportFilters] = useState({
    type: { value: -1, label: "Select report" },
  });
  const [reportInProgress, setReportInProgress] = useState(false);
  const [showReports, setShowReports] = useState(false);
  const [subItemsLoading, setSubItemsLoading] = useState(false);
  const [packagingSubItems, setPackagingSubItems] = useState([]);
  const [palletExpandChildren, setPalletExpandChildren] = useState({});
  const [palletExpandLoading, setPalletExpandLoading] = useState({});
  const [productsForDropdown, setProductsForDropdown] = useState([]);
  const [productTypeOptions, setProductTypeOptions] = useState([]);

  const getAnalytics = async function (search, filterArg) {
    setAnalytics({ ...analytics, loading: true });
    const payload = {
      filters: { ...filterArg, search: search },
    };
    let callResult = await makePostApiCall(`fetchPackagingAnalytics`, payload);
    setAnalytics({ ...analytics, loading: false });
    if (callResult.success) {
      setAnalytics({ ...analytics, ...callResult.data });
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getPackagings = async function (search, page, filterArg) {
    setLoading(true);
    const payload = {
      page: page,
      filters: { ...filterArg, search: search },
    };
    getAnalytics(search, filterArg);
    let callResult = await makePostApiCall(`fetchAllPackagingRecords`, payload);
    setLoading(false);
    if (callResult.success) {
      setPackagings(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getPackagingSubItems = async function (
    plu,
    lot_number,
    packaging_date,
  ) {
    setPackagingSubItems([]);
    setPalletExpandChildren({});
    setPalletExpandLoading({});
    setSubItemsLoading(true);

    // API expects POST with payload containing the identifiers.
    const payload = {
      plu,
      lot_number,
      packaging_date,
    };

    let callResult = await makePostApiCall(`getPackagingSubItems`, payload);
    if (callResult.success) {
      const items = Array.isArray(callResult.data)
        ? callResult.data
        : (callResult.data?.cartons ?? []);
      setPackagingSubItems(items);
      setSubItemsLoading(false);
      return items;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
    setSubItemsLoading(false);
    return [];
  };

  const fetchPalletExpand = async function (barcode) {
    setPalletExpandLoading((prev) => ({ ...prev, [barcode]: true }));
    const path = `pallet/${encodeURIComponent(barcode)}/expand`;
    let callResult = await makeGetApiCall(path);
    if (callResult.success) {
      // API returns { cartons: [...] }
      const list = Array.isArray(callResult.data)
        ? callResult.data
        : (callResult.data?.cartons ?? []);
      setPalletExpandChildren((prev) => ({ ...prev, [barcode]: list }));
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
    setPalletExpandLoading((prev) => ({ ...prev, [barcode]: false }));
  };

  const deletePackaging = async function (barcode, index) {
    setLoading(true);
    const payload = {
      shipping_barcode: barcode,
    };
    let callResult = await makePostApiCall(
      `deleteSinglePackagedItemByBarcode`,
      payload,
    );
    setLoading(false);
    if (callResult.success) {
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });

      var newPackagingSubItems = packagingSubItems.filter(
        (item) => item.barcode !== barcode,
      );
      setPackagingSubItems(newPackagingSubItems);
      setPalletExpandChildren((prev) => {
        const next = { ...prev };
        for (const k of Object.keys(next)) {
          next[k] = (next[k] || []).filter((item) => item.barcode !== barcode);
        }
        return next;
      });
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message,
          SnackbarType.Success,
        ),
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const askDeletePackaging = function (packaging, index) {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={() => deletePackaging(packaging.barcode, index)}
          message={`Are you sure you want to delete ${packaging.product_name} - Serial: ${packaging.serial_number}?`}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  const deletePackagingLot = async function (lot, packaging_date) {
    setLoading(true);

    let callResult = await makePostApiCall(`deleteEntirePackaging/${lot}`, {
      packaging_date: packaging_date,
    });
    setLoading(false);
    if (callResult.success) {
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });
      getPackagings(null, 0, filters);

      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message,
          SnackbarType.Success,
        ),
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const askDeletePackagingLot = function (packaging, index) {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={() =>
            deletePackagingLot(packaging.lot_number, packaging.packaging_date)
          }
          message={`Are you sure you want to delete ${packaging.product_name} - Lot: ${packaging.lot_number}?`}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  const deleteMultiplePackaging = async function (records) {
    let callResult = await makePostApiCall("deleteMultiplePackaging", {
      packaging_records: records,
    });

    if (callResult.success) {
      setEmptyModelOpen({ open: false, childComponent: null });

      const skipped = callResult.data?.not_deleted_packaging_records ?? [];
      const skippedKeys = new Set(skipped.map((r) => `${r.type}_${r.id}`));
      const deletedKeys = new Set(
        records
          .filter((r) => !skippedKeys.has(`${r.type}_${r.id}`))
          .map((r) => `${r.type}_${r.id}`),
      );

      setPackagingSubItems((prev) =>
        prev.filter(
          (item) =>
            !deletedKeys.has(
              `${
                String(item.carton_type ?? "").toLowerCase() === "pallet"
                  ? "pallet"
                  : "carton"
              }_${item.id}`,
            ),
        ),
      );
      setPalletExpandChildren((prev) => {
        const next = { ...prev };
        for (const k of Object.keys(next)) {
          next[k] = (next[k] || []).filter(
            (item) => !deletedKeys.has(`carton_${item.id}`),
          );
        }
        return next;
      });

      getPackagings(null, 0, filters);

      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message,
          SnackbarType.Success,
        ),
      );

      if (callResult.data?.not_deleted) {
        showErrorModal(
          "Some records were not deleted",
          `These records were skipped because they have already been shipped: ${skipped
            .map((r) => `${r.type} ${r.barcode ?? r.id}`)
            .join(", ")}`,
        );
      }
    } else if (callResult.errorStatus === 401) {
      logout();
    }

    return callResult;
  };

  const packagingReprintAll = async function (lot, packagingDate) {
    const payload = { packaging_date: packagingDate };
    setBulkActionInProgress({
      reprintAllLot: lot,
      barcode: "",
    });
    let callResult = await makePostApiCall(
      `reprintPackagingByLot/${lot}`,
      payload,
    );

    if (callResult.success) {
      if (callResult.data.number_of_label_to_print > 1) {
        window.open(callResult.data.location, "_blank");
      } else {
        const item = callResult.data.items[0];
        setPackagingLabel({
          brand_image1: item.brand_image1,
          brand_image2: item.brand_image2,
          brand_image3: item.brand_image3,
          brand_name: item.brand_name,
          address: item.address,
          lot_number: item.lot_number,
          product_plu: item.product_plu,
          ingredients: item.ingredients,
          product_name: item.product_name,
          product_description: item.product_description,
          passed_from_metal_detector: item.passed_from_metal_detector,
          product_weight: item.product_weight + "kg",
          barcodeHtml: item.barcodeHtml,
          barcode: item.barcode,
          production_date: item.production_date,
          best_before_date: item.best_before_date,
          product_barcode: item.product_barcode,
          product_barcode_html: item.product_barcode_html,
        });
        // const labels = {
        //   items: callResult.data.items,
        //   printCounter: 1,
        // };
        setTimeout(function () {
          window.print();
          // printPackagingLabelRecursive(labels);
        }, 250);
      }
    }

    setBulkActionInProgress({
      reprintAllLot: null,
      barcode: "",
    });
  };

  const reprintSinglePackagingLabel = async function (barcode) {
    setBulkActionInProgress({
      reprintAllLot: null,
      barcode: barcode,
    });
    let callResult = await makePostApiCall(
      `reprintPackagingByBarcode/${barcode}`,
    );

    if (callResult.success) {
      const labelDetails = callResult.data?.label_details ?? {};
      const element =
        labelDetails.carton_labels?.[0] ?? labelDetails.pallet_label?.[0];

      if (!element) {
        showErrorModal(
          "Reprint failed",
          "Could not load label data for reprint.",
        );
      } else {
        const printPayload = {
          ...element,
          net_weight: `${element.net_weight ?? ""}${element.unit_of_measure ?? ""}`,
          passed_from_metal_detector: Boolean(
            element.passed_from_metal_detector,
          ),
        };
        const printResult = await makePostApiCallToPrinter(printPayload);
        if (!printResult?.success) {
          const errorMessage =
            printResult?.data?.subtitle ??
            printResult?.data?.message ??
            printResult?.data?.title ??
            "Print failed. Please try again.";
          showErrorModal("Print error", errorMessage);
        }
      }
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      } else {
        showErrorModal(
          "Reprint failed",
          callResult.data?.message ??
            callResult.data?.subtitle ??
            "Could not load label data for reprint.",
        );
      }
    }

    setBulkActionInProgress({
      reprintAllLot: null,
      barcode: "",
    });
  };

  const openPackagingDetailsSideView = async function (lot_number, plu) {
    let callResult = await makePostApiCall(`fetchPackagingRecord`, {
      lot_number: lot_number,
      plu: plu,
    });
    if (callResult.success) {
      const packagedItems = Array.isArray(callResult.data)
        ? callResult.data
        : (callResult.data?.packaged_material ?? []);

      setSlideOver({
        childComponent: (
          <PackagingDetails
            packagedItem={packagedItems}
            setOpen={setSlideOver}
          />
        ),
        open: true,
      });
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getEmployeesForDropdown = async function (type, department) {
    const payload = { department: department, type: [type] };
    let callResult = await makePostApiCall(`employeesForDropdown`, payload);

    if (callResult.success) {
      if (type === 1) {
        setEmployeeOptions(callResult.data);
      } else {
        setSupervisorOptions(callResult.data);
      }
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getProductTypes = async function () {
    let callResult = await makePostApiCall(`productTypesForDropdown`);

    if (callResult.success) {
      setProductTypeOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const fetchBrandsForDropdown = async function () {
    let callResult = await makePostApiCall(`brandsForDropdown`);

    if (callResult.success) {
      setBrands([{ value: -1, label: "All Brands" }, ...callResult.data]);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const fetchProductsForDropdown = async function () {
    let callResult = await makePostApiCall(`fetchProductsForDropdown`);

    if (callResult.success) {
      setProductsForDropdown([
        { value: -1, label: "All Products" },
        ...callResult.data,
      ]);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const resetFilters = () => {
    setFilters(defaultFilters);

    setShowFilters(false);
    getPackagings(null, 1, defaultFilters);
  };

  const filterComponents = [
    <FilterSingleSelectDropdown
      header="Product"
      onChange={(value) =>
        setFilters({
          ...filters,
          product: value,
        })
      }
      options={productsForDropdown}
      resetFilter={() =>
        setFilters({
          ...filters,
          product: { value: -1, label: "All Products" },
        })
      }
      valueName="product"
      value={filters.product}
    />,
    <FilterSingleSelectDropdown
      header="Category"
      onChange={(value) =>
        setFilters({
          ...filters,
          category: value,
        })
      }
      options={productTypeOptions}
      resetFilter={() =>
        setFilters({
          ...filters,
          category: { value: -1, label: "All Categories" },
        })
      }
      valueName="category"
      value={filters.category}
    />,
    <FilterSingleSelectDropdown
      header="Brand"
      onChange={(value) =>
        setFilters({
          ...filters,
          brand: value,
        })
      }
      options={brands}
      resetFilter={() =>
        setFilters({
          ...filters,
          brand: { value: -1, label: "All Brands" },
        })
      }
      valueName="brand"
      value={filters.brand}
    />,
    <FilterSingleSelectDropdown
      header="Supervisor"
      onChange={(value) =>
        setFilters({
          ...filters,
          supervisor: value,
        })
      }
      options={supervisorOptions}
      resetFilter={() =>
        setFilters({
          ...filters,
          supervisor: { value: -1, label: "All Employees" },
        })
      }
      valueName="supervisor"
      value={filters.supervisor}
    />,
    <FilterSingleSelectDropdown
      header="Employee 1"
      onChange={(value) =>
        setFilters({
          ...filters,
          employee1: value,
        })
      }
      options={employeeOptions}
      resetFilter={() =>
        setFilters({
          ...filters,
          employee1: { value: -1, label: "All Employees" },
        })
      }
      valueName="employee1"
      value={filters.employee1}
    />,
  ];

  const generatePackagingReport = async function () {
    setReportInProgress(true);
    let callResult = await makePostApiCall(`generatePackagingReport`, {
      ...reportFilters,
      startDate: filters.startDate,
      endDate: filters.endDate,
    });
    if (callResult.success) {
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });
      window.open(callResult.data.location, "_blank");
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
    setReportInProgress(false);
  };

  const reportComponents = [
    <FilterSingleSelectDropdown
      header="Report type"
      onChange={(value) =>
        setReportFilters({
          ...reportFilters,
          type: value,
        })
      }
      options={reportTypeOptions}
      resetFilter={() =>
        setReportFilters({
          ...reportFilters,
          type: { value: -1, label: "Select report" },
        })
      }
      valueName="type"
      value={reportFilters.type}
    />,
  ];

  return {
    askDeletePackaging,
    emptyModal,
    setEmptyModelOpen,
    getPackagings,
    loading,
    packagings,
    setSlideOver,
    slideOver,
    packagingReprintAll,
    printingLabelType,
    packagingLabel,
    bulkActionInProgress,
    reprintSinglePackagingLabel,
    askDeletePackagingLot,
    deleteMultiplePackaging,
    openPackagingDetailsSideView,
    fetchBrandsForDropdown,
    getEmployeesForDropdown,
    filters,
    showFilters,
    resetFilters,
    filterComponents,
    setShowFilters,
    generatePackagingReport,
    reportComponents,
    showReports,
    setShowReports,
    reportInProgress,
    analytics,
    getPackagingSubItems,
    fetchPalletExpand,
    packagingSubItems,
    subItemsLoading,
    palletExpandChildren,
    palletExpandLoading,
    fetchProductsForDropdown,
    getProductTypes,
    setShowDates,
    showDates,
    setFilters,
  };
}
